import { parseSyncJsonBody } from "./scheduleSyncGate";
import { normalizeSyncBaseUrl } from "./syncSettings";
import { parseSnapshot, type SyncSnapshot } from "./sync";
import { normalizeInviteCode } from "./company";
import { boardPushErrorMessage } from "./companySync";
import type { SyncLiveSlice, SyncRevisionMeta } from "./syncRevision";
import { liveSimSource, liveSliceFromSnapshot } from "./syncRevision";

export type SyncHealthResult =
  | { ok: true; service: string }
  | { ok: false; error: string; status?: number };

export type SyncPullResult =
  | { ok: true; snapshot: SyncSnapshot }
  | { ok: false; error: string; status?: number };

export type SyncPushResult =
  | { ok: true; snapshotId?: string; retained?: number }
  | { ok: false; error: string; status?: number };

/** Confirmed manual Push may overwrite a fatter/divergent Inbox. Auto-Push omits this. */
export const NANODE_OVERWRITE_INBOX_HEADER = "X-Northstar-Overwrite";
export const NANODE_OVERWRITE_INBOX_VALUE = "inbox";

export type PushSyncStateOptions = {
  overwriteInbox?: boolean;
};

export type RemoteSnapshotMeta = {
  id: string;
  scope: "company" | "account";
  savedAtUtc: string;
  company: string;
  cashCad: number;
  currency: string;
  missionCount: number;
  includeSecrets: boolean;
};

export type RemoteSnapshotListResult =
  | { ok: true; snapshots: RemoteSnapshotMeta[]; retention: number }
  | { ok: false; error: string; status?: number };

export type RemoteSnapshotGetResult =
  | { ok: true; id: string; snapshot: SyncSnapshot }
  | { ok: false; error: string; status?: number };

export type RemoteCompanySummary = {
  id: string;
  name: string;
  homeIcao: string;
  extraBases: string[];
  crew: { id: string; displayName: string; roles: string[]; isAdmin: boolean }[];
};

export type InviteLookupResult =
  | { ok: true; name: string; homeIcao: string }
  | { ok: false; error: string; status?: number };

export type RemoteJoinResult =
  | { ok: true; crewMember: { id: string; displayName: string; roles: string[] }; company: RemoteCompanySummary }
  | { ok: false; error: string; status?: number };

export type RemoteLeaveResult =
  | { ok: true; crewId: string }
  | { ok: false; error: string; status?: number };

export type SyncRevisionResult =
  | { ok: true; revision: SyncRevisionMeta }
  | { ok: false; error: string; status?: number };

export type SyncLivePullResult =
  | { ok: true; live: SyncLiveSlice }
  | { ok: false; error: string; status?: number };

const FETCH_MS = 8000;

function joinUrl(baseUrl: string, path: string): string {
  return `${normalizeSyncBaseUrl(baseUrl)}${path}`;
}

async function fetchTimed(url: string, init: RequestInit, timeoutMs = FETCH_MS): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

function authHeaders(token: string): HeadersInit {
  return {
    Accept: "application/json",
    Authorization: `Bearer ${token.trim()}`,
  };
}

function failMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) {
    if (err.name === "AbortError") return "Sync server timed out.";
    return err.message || fallback;
  }
  return fallback;
}

/** Unauthenticated probe. Does not prove the token — only that something answers /health. */
export async function checkSyncHealth(baseUrl: string): Promise<SyncHealthResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url) return { ok: false, error: "Enter a sync server base URL." };
  try {
    const res = await fetchTimed(joinUrl(url, "/health"), { method: "GET", headers: { Accept: "application/json" } });
    if (!res.ok) {
      return { ok: false, error: `Health HTTP ${res.status}`, status: res.status };
    }
    let body: { ok?: boolean; service?: string } | null = null;
    try {
      const parsed = await res.json();
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) body = parsed as { ok?: boolean; service?: string };
    } catch {
      body = null;
    }
    if (!body) return { ok: false, error: "The server returned an unexpected page.", status: res.status };
    if (body.ok === false) {
      return { ok: false, error: "Health reported not ok." };
    }
    return { ok: true, service: typeof body.service === "string" ? body.service : "sync" };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Sync server unreachable.") };
  }
}

/**
 * Confirm-gated Nanode Pull. Same GET `/v1/state` as desk Pull & replace.
 * Does not write the board — the caller applies with `replaceFromSnapshot`,
 * so local secrets stay and this does not become a second snapshot writer.
 * Unconfirmed, a bad token, and an empty URL fail closed and do not call `apply`.
 */
export async function applyConfirmedSyncPull(input: {
  confirmed: boolean;
  baseUrl: string;
  token: string;
  /** Desk button already fetched this snapshot for the confirm summary. */
  snapshot?: SyncSnapshot;
  apply: (snapshot: SyncSnapshot) => void;
}): Promise<SyncPullResult> {
  if (input.confirmed !== true) {
    return { ok: false, error: "Confirm before pulling the Nanode company snapshot." };
  }
  const pulled = input.snapshot
    ? { ok: true as const, snapshot: input.snapshot }
    : await pullSyncState(input.baseUrl, input.token);
  if (!pulled.ok) return pulled;
  input.apply(pulled.snapshot);
  return pulled;
}

export async function pullSyncState(baseUrl: string, token: string): Promise<SyncPullResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url || !token.trim()) {
    return { ok: false, error: "Sync server URL and API token are required." };
  }
  try {
    const res = await fetchTimed(joinUrl(url, "/v1/state"), { method: "GET", headers: authHeaders(token) });
    const text = await readResponseText(res);
    if (res.status === 401) return { ok: false, error: "Unauthorized — check the API token.", status: 401 };
    if (res.status === 404) return { ok: false, error: "Remote has no snapshot yet. Push first.", status: 404 };
    if (!res.ok) return { ok: false, error: `Pull HTTP ${res.status}`, status: res.status };
    const body = parseSyncJsonBody(res.status, text);
    if (!body.ok) return { ok: false, error: body.error, status: res.status };
    const parsed = parseSnapshot(body.value);
    if (!parsed.ok) return { ok: false, error: parsed.error };
    return { ok: true, snapshot: parsed.snapshot };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Pull failed.") };
  }
}

/** Real responses expose text(). Older test doubles only expose json(). */
async function readResponseText(res: { text?: () => Promise<string>; json?: () => Promise<unknown> }): Promise<string> {
  if (typeof res.text === "function") {
    try {
      const text = await res.text();
      return typeof text === "string" ? text : "";
    } catch {
      return "";
    }
  }
  if (typeof res.json === "function") {
    try {
      const value = await res.json();
      if (value == null) return "";
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  return "";
}

function parseRevisionBody(raw: unknown): SyncRevisionMeta | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  if (typeof rec.scheduleRevision !== "string" || typeof rec.liveRevision !== "string") return null;
  return {
    scheduleRevision: rec.scheduleRevision,
    liveRevision: rec.liveRevision,
    exportedAtUtc: typeof rec.exportedAtUtc === "string" ? rec.exportedAtUtc : "",
    companyId: typeof rec.companyId === "string" ? rec.companyId : "",
    companyName: typeof rec.companyName === "string" ? rec.companyName : "",
    theme: rec.theme === "light" || rec.theme === "dark" ? rec.theme : "dark",
    themePack: typeof rec.themePack === "string" ? rec.themePack : "",
    themeAccent: typeof rec.themeAccent === "string" ? rec.themeAccent : "",
    deskVersion: typeof rec.deskVersion === "string" ? rec.deskVersion : "",
  };
}

/** Cheap ping — schedule vs live hashes, not the full board. */
export async function fetchSyncRevision(baseUrl: string, token: string): Promise<SyncRevisionResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url || !token.trim()) {
    return { ok: false, error: "Sync server URL and API token are required." };
  }
  try {
    const res = await fetchTimed(joinUrl(url, "/v1/revision"), { method: "GET", headers: authHeaders(token) }, 4000);
    const text = await readResponseText(res);
    if (res.status === 401) return { ok: false, error: "Unauthorized — check the API token.", status: 401 };
    if (res.status === 404) return { ok: false, error: "Remote has no snapshot yet. Push first.", status: 404 };
    if (!res.ok) return { ok: false, error: `Revision HTTP ${res.status}`, status: res.status };
    const body = parseSyncJsonBody(res.status, text);
    if (!body.ok) return { ok: false, error: body.error, status: res.status };
    const parsed = parseRevisionBody(body.value);
    if (!parsed) return { ok: false, error: "Revision payload was not recognized." };
    return { ok: true, revision: parsed };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Revision ping failed.") };
  }
}

export async function pullSyncLive(
  baseUrl: string,
  token: string,
  opts: { trail?: boolean } = {},
): Promise<SyncLivePullResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url || !token.trim()) {
    return { ok: false, error: "Sync server URL and API token are required." };
  }
  try {
    const path = opts.trail ? "/v1/live?trail=1" : "/v1/live";
    const res = await fetchTimed(joinUrl(url, path), { method: "GET", headers: authHeaders(token) }, 4000);
    const text = await readResponseText(res);
    if (res.status === 401) return { ok: false, error: "Unauthorized — check the API token.", status: 401 };
    if (res.status === 404) return { ok: false, error: "Remote has no snapshot yet. Push first.", status: 404 };
    if (!res.ok) return { ok: false, error: `Live HTTP ${res.status}`, status: res.status };
    const body = parseSyncJsonBody(res.status, text);
    if (!body.ok) return { ok: false, error: body.error, status: res.status };
    const raw: unknown = body.value;
    if (!raw || typeof raw !== "object") return { ok: false, error: "Live payload was not recognized." };
    const rec = raw as Record<string, unknown>;
    return {
      ok: true,
      live: {
        liveRevision: typeof rec.liveRevision === "string" ? rec.liveRevision : liveSliceFromSnapshot(raw).liveRevision,
        exportedAtUtc: typeof rec.exportedAtUtc === "string" ? rec.exportedAtUtc : "",
        crewPresence: rec.crewPresence ?? [],
        lastKnownPosition: rec.lastKnownPosition ?? null,
        simSource: liveSimSource(raw),
        trailRevision: typeof rec.trailRevision === "string" ? rec.trailRevision : "",
        flightTrack: rec.flightTrack ?? null,
      },
    };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Live pull failed.") };
  }
}

export async function pushSyncLive(
  baseUrl: string,
  token: string,
  slice: {
    crewPresence?: unknown;
    lastKnownPosition?: unknown;
    exportedAtUtc?: string;
    /** Active trail. Omit on kinematics-only ticks so Nanode is not rewritten with geometry every second. */
    flightTrack?: unknown;
    simSource?: "simconnect" | "simulated" | "none";
  },
): Promise<SyncPushResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url || !token.trim()) {
    return { ok: false, error: "Sync server URL and API token are required." };
  }
  try {
    const res = await fetchTimed(
      joinUrl(url, "/v1/live"),
      {
        method: "PUT",
        headers: {
          ...authHeaders(token),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(slice),
      },
      4000,
    );
    if (res.status === 401) return { ok: false, error: "Unauthorized — check the API token.", status: 401 };
    if (res.status === 404) return { ok: false, error: "Remote has no snapshot yet. Push first.", status: 404 };
    if (!res.ok) return { ok: false, error: `Live push HTTP ${res.status}`, status: res.status };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Live push failed.") };
  }
}

export async function pushSyncState(
  baseUrl: string,
  token: string,
  snapshot: SyncSnapshot,
  opts: PushSyncStateOptions = {},
): Promise<SyncPushResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url || !token.trim()) {
    return { ok: false, error: "Sync server URL and API token are required." };
  }
  try {
    const res = await fetchTimed(joinUrl(url, "/v1/state"), {
      method: "PUT",
      headers: {
        ...authHeaders(token),
        "Content-Type": "application/json",
        ...(opts.overwriteInbox
          ? { [NANODE_OVERWRITE_INBOX_HEADER]: NANODE_OVERWRITE_INBOX_VALUE }
          : {}),
      },
      body: JSON.stringify(snapshot),
    });
    if (res.status === 401) return { ok: false, error: "Unauthorized — check the API token.", status: 401 };
    if (res.status === 403 || res.status === 409) {
      const refused = (await res.json().catch(() => ({}))) as { error?: string };
      const code = typeof refused.error === "string" ? refused.error : "";
      if (code === "follower_push" || code === "stale_desk" || code === "orphan_clobber") {
        return { ok: false, error: boardPushErrorMessage(code), status: res.status };
      }
      return {
        ok: false,
        error:
          "Nanode refused a fatter different Inbox (Book/demo). Pull on this desk, or confirm Push on the sim ops desk.",
        status: res.status,
      };
    }
    if (!res.ok) return { ok: false, error: `Push HTTP ${res.status}`, status: res.status };
    const body = (await res.json().catch(() => ({}))) as { snapshotId?: string; retained?: number };
    return {
      ok: true,
      snapshotId: typeof body.snapshotId === "string" ? body.snapshotId : undefined,
      retained: Number.isFinite(body.retained) ? body.retained : undefined,
    };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Push failed.") };
  }
}

/** Schedule-only publish. Any desk (Main or Secondary) may call this. No API keys. */
export type ScheduleMergeRequest = {
  missions?: unknown[];
  assignments?: unknown[];
  removedMissionIds?: string[];
  theme?: string;
  themePack?: string;
  themeAccent?: string;
  deskChrome?: { theme?: string; themePack?: string; themeAccent?: string };
  company?: { id?: string; operatorName?: string; homeIcao?: string };
  crew?: unknown[];
  connectedDevices?: unknown[];
  deskVersion?: string;
};

export async function pushScheduleMerge(
  baseUrl: string,
  token: string,
  body: ScheduleMergeRequest,
): Promise<SyncPushResult & { missionCount?: number }> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url || !token.trim()) {
    return { ok: false, error: "Sync server URL and API token are required." };
  }
  try {
    const res = await fetchTimed(joinUrl(url, "/v1/schedule"), {
      method: "POST",
      headers: { ...authHeaders(token), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const text = await readResponseText(res);
    if (res.status === 401) return { ok: false, error: "Unauthorized — check the API token.", status: 401 };
    if (!res.ok) {
      const parsed = parseSyncJsonBody(res.status, text);
      const code = parsed.ok && parsed.value && typeof parsed.value === "object" ? (parsed.value as { error?: string }).error : "";
      return { ok: false, error: code || `Schedule push HTTP ${res.status}`, status: res.status };
    }
    const parsed = parseSyncJsonBody(res.status, text);
    if (!parsed.ok) return { ok: false, error: parsed.error, status: res.status };
    const rec = parsed.value && typeof parsed.value === "object" ? (parsed.value as { missionCount?: number }) : {};
    return { ok: true, missionCount: typeof rec.missionCount === "number" ? rec.missionCount : undefined };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Schedule push failed.") };
  }
}

function parseRemoteMetas(raw: unknown): RemoteSnapshotMeta[] {
  const rows = Array.isArray(raw) ? raw : [];
  const out: RemoteSnapshotMeta[] = [];
  for (const item of rows) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    if (typeof rec.id !== "string" || !rec.id.trim()) continue;
    out.push({
      id: rec.id,
      scope: rec.scope === "account" ? "account" : "company",
      savedAtUtc: typeof rec.savedAtUtc === "string" ? rec.savedAtUtc : "",
      company: typeof rec.company === "string" && rec.company.trim() ? rec.company : "—",
      cashCad: Number.isFinite(rec.cashCad) ? Number(rec.cashCad) : 0,
      currency: typeof rec.currency === "string" && rec.currency.trim() ? rec.currency : "CAD",
      missionCount: Number.isFinite(rec.missionCount) ? Number(rec.missionCount) : 0,
      includeSecrets: Boolean(rec.includeSecrets),
    });
  }
  return out;
}

/** Dated company Push copies. Same SYNC_TOKEN as /v1/state. */
export async function listRemoteSnapshots(baseUrl: string, token: string): Promise<RemoteSnapshotListResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url || !token.trim()) {
    return { ok: false, error: "Sync server URL and API token are required." };
  }
  try {
    const res = await fetchTimed(joinUrl(url, "/v1/snapshots"), { method: "GET", headers: authHeaders(token) });
    if (res.status === 401) return { ok: false, error: "Unauthorized — check the API token.", status: 401 };
    if (res.status === 404) return { ok: false, error: "This sync server does not list dated snapshots yet.", status: 404 };
    if (!res.ok) return { ok: false, error: `Snapshots HTTP ${res.status}`, status: res.status };
    const body = (await res.json().catch(() => ({}))) as { ok?: boolean; snapshots?: unknown; retention?: number };
    return {
      ok: true,
      snapshots: parseRemoteMetas(body.snapshots),
      retention: Number.isFinite(body.retention) ? Number(body.retention) : 16,
    };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Snapshot list failed.") };
  }
}

export async function getRemoteSnapshot(
  baseUrl: string,
  token: string,
  id: string,
): Promise<RemoteSnapshotGetResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url || !token.trim() || !id.trim()) {
    return { ok: false, error: "Sync server URL, API token, and snapshot id are required." };
  }
  try {
    const res = await fetchTimed(joinUrl(url, `/v1/snapshots/${encodeURIComponent(id)}`), {
      method: "GET",
      headers: authHeaders(token),
    });
    if (res.status === 401) return { ok: false, error: "Unauthorized — check the API token.", status: 401 };
    if (res.status === 404) return { ok: false, error: "That dated snapshot is gone.", status: 404 };
    if (!res.ok) return { ok: false, error: `Snapshot HTTP ${res.status}`, status: res.status };
    const body = (await res.json().catch(() => null)) as { ok?: boolean; id?: string; snapshot?: unknown } | null;
    const parsed = parseSnapshot(body?.snapshot ?? body);
    if (!parsed.ok) return { ok: false, error: parsed.error };
    return { ok: true, id: typeof body?.id === "string" ? body.id : id, snapshot: parsed.snapshot };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Snapshot fetch failed.") };
  }
}

/** Unauthenticated invite peek — only company name + home base, never the code. */
export async function lookupCompanyInvite(baseUrl: string, inviteCode: string): Promise<InviteLookupResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  const code = normalizeInviteCode(inviteCode);
  if (!url || !code) return { ok: false, error: "Sync URL and invite code are required." };
  try {
    const res = await fetchTimed(joinUrl(url, `/v1/invite/${encodeURIComponent(code)}`), {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (res.status === 404) return { ok: false, error: "Invite code not found on that sync server.", status: 404 };
    if (!res.ok) return { ok: false, error: `Invite HTTP ${res.status}`, status: res.status };
    const body = (await res.json().catch(() => ({}))) as { ok?: boolean; name?: string; homeIcao?: string };
    if (!body.ok || !body.name) return { ok: false, error: "Invite lookup failed." };
    return { ok: true, name: body.name, homeIcao: typeof body.homeIcao === "string" ? body.homeIcao : "" };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Invite lookup failed.") };
  }
}

/** Join via invite code. The code is the secret — no API token required. */
export async function joinCrewRemote(
  baseUrl: string,
  input: { inviteCode: string; displayName: string; role: "dispatcher" | "pilot" },
): Promise<RemoteJoinResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url) return { ok: false, error: "Enter a sync server base URL." };
  if (!input.inviteCode.trim() || !input.displayName.trim()) {
    return { ok: false, error: "Invite code and crew name are required." };
  }
  try {
    const res = await fetchTimed(joinUrl(url, "/v1/crew/join"), {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({
        inviteCode: normalizeInviteCode(input.inviteCode),
        displayName: input.displayName.trim(),
        role: input.role,
      }),
    });
    if (res.status === 403) return { ok: false, error: "Invite code does not match this company.", status: 403 };
    if (res.status === 404) return { ok: false, error: "Remote has no company snapshot yet. Ask them to Push first.", status: 404 };
    if (!res.ok) return { ok: false, error: `Join HTTP ${res.status}`, status: res.status };
    const body = (await res.json().catch(() => null)) as RemoteJoinResult | null;
    if (!body || !("ok" in body) || !body.ok) {
      return { ok: false, error: "Join failed." };
    }
    return body;
  } catch (err) {
    return { ok: false, error: failMessage(err, "Join failed.") };
  }
}

export type BoardTicketWriteInput = {
  action: "complete" | "cancel" | "edit" | "reassign_crew" | "duty";
  missionId?: string;
  cancelReason?: string;
  cancelReasonNote?: string;
  departureIso?: string;
  arrivalIso?: string;
  paxCount?: number;
  cargoLb?: number;
  crewHint?: string;
  acceptMissingScenery?: boolean;
  crewId?: string;
  duty?: unknown;
};

export type BoardTicketWriteResult =
  | { ok: true; summary: string; missionId: string }
  | { ok: false; error: string; status?: number };

/** Pilot-allowed / scoped board write. Does not replace the Dispatch company snapshot. */
export async function writeBoardTicket(
  baseUrl: string,
  token: string,
  input: BoardTicketWriteInput,
): Promise<BoardTicketWriteResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url || !token.trim()) {
    return { ok: false, error: "Sync server URL and API token are required." };
  }
  try {
    const res = await fetchTimed(joinUrl(url, "/v1/board/ticket"), {
      method: "POST",
      headers: {
        ...authHeaders(token),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    });
    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      error?: string;
      summary?: string;
      missionId?: string;
    };
    if (!res.ok || body.ok === false) {
      return { ok: false, error: body.error || `Board write failed (${res.status})`, status: res.status };
    }
    return {
      ok: true,
      summary: body.summary || "Board updated.",
      missionId: body.missionId || input.missionId || "",
    };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Board write failed.") };
  }
}

/** Leave via invite code (or Bearer on the server). Does not wipe the company snapshot. */
export async function leaveCrewRemote(
  baseUrl: string,
  input: { inviteCode: string; crewId: string; token?: string },
): Promise<RemoteLeaveResult> {
  const url = normalizeSyncBaseUrl(baseUrl);
  if (!url) return { ok: false, error: "Enter a sync server base URL." };
  if (!input.crewId.trim()) return { ok: false, error: "Crew id is required." };
  try {
    const headers: Record<string, string> = { Accept: "application/json", "Content-Type": "application/json" };
    if (input.token?.trim()) headers.Authorization = `Bearer ${input.token.trim()}`;
    const res = await fetchTimed(joinUrl(url, "/v1/crew/leave"), {
      method: "POST",
      headers,
      body: JSON.stringify({
        inviteCode: normalizeInviteCode(input.inviteCode),
        crewId: input.crewId.trim(),
      }),
    });
    if (res.status === 403) return { ok: false, error: "Invite code does not match this company.", status: 403 };
    if (res.status === 404) return { ok: false, error: "That crew member is not on the remote roster.", status: 404 };
    if (!res.ok) return { ok: false, error: `Leave HTTP ${res.status}`, status: res.status };
    const body = (await res.json().catch(() => null)) as { ok?: boolean; crewId?: string } | null;
    if (!body?.ok || !body.crewId) return { ok: false, error: "Leave failed." };
    return { ok: true, crewId: body.crewId };
  } catch (err) {
    return { ok: false, error: failMessage(err, "Leave failed.") };
  }
}
